import { useEffect } from 'react';
import { getState, useStore } from '../store/store';
import { num } from '../geo/format';

// Deep links: /m/<map-slug>?f=<feature key>  or  /m/<map-slug>?x=..&z=..&zoom=..
// Game data: /data/<category>?r=<asset guid or record id> (kept in step by data/DataWorkspace.tsx).
// PocketBase serves index.html for unknown paths (SPA fallback), so these survive a reload.

export interface UrlState {
  view: 'map' | 'data';
  map: string | null;
  feature: string | null;
  x: number | null;
  z: number | null;
  zoom: number | null;
  dataCategory: string | null;
  record: string | null;
}

export function readUrlState(): UrlState {
  const m = /^\/m\/([a-z0-9_-]+)/.exec(location.pathname);
  const d = /^\/data(?:\/([a-z0-9_]+))?\/?$/.exec(location.pathname);
  const q = new URLSearchParams(location.search);
  const n = (k: string) =>
    q.get(k) !== null && q.get(k) !== '' && isFinite(Number(q.get(k))) ? Number(q.get(k)) : null;
  return {
    view: d ? 'data' : 'map',
    map: m?.[1] ?? null,
    feature: q.get('f'),
    x: n('x'),
    z: n('z'),
    zoom: n('zoom'),
    dataCategory: d?.[1] ?? null,
    record: d ? q.get('r') : null,
  };
}

export function featureLink(featureKey: string): string {
  const s = getState();
  const slug = s.maps.find((m) => m.id === s.mapId)?.slug ?? '';
  return `${location.origin}/m/${slug}?f=${encodeURIComponent(featureKey)}`;
}

export function pointLink(x: number, z: number, zoom: number): string {
  const s = getState();
  const slug = s.maps.find((m) => m.id === s.mapId)?.slug ?? '';
  return `${location.origin}/m/${slug}?x=${num(x, 1)}&z=${num(z, 1)}&zoom=${num(zoom, 2)}`;
}

/** Keeps the address bar pointing at the current map + selected feature (map view only). */
export function useUrlSync() {
  const slug = useStore((s) => s.maps.find((m) => m.id === s.mapId)?.slug);
  const key = useStore((s) => (s.selectedId ? s.features[s.selectedId]?.key : undefined));
  const view = useStore((s) => s.view);
  useEffect(() => {
    if (!slug || view !== 'map') return;
    const url = `/m/${slug}${key ? `?f=${encodeURIComponent(key)}` : ''}`;
    if (location.pathname + location.search !== url) history.replaceState(null, '', url);
  }, [slug, key, view]);
}
